import type { Booking, BookingsApiOptions, BookingsApiPayload } from '@brighterly/lib-core-types/booking/interfaces'

import { SortDirection } from '@brighterly/lib-core-types/options/enums'

import { useBookingStore } from '@stores/booking'

import { useCsRoutes } from '@composables/routes/cs-route.hook'

import { listBookingRequestStatuses, listFallbackBookingRequestStatuses } from '@config/booking'
import { isFullArray } from '@helpers/array'
import { pick } from '@helpers/object'

export const useBookingsApiDemo = async (payload: BookingsApiPayload, options?: BookingsApiOptions): Promise<Booking[] | null> => {
  const { $axios } = useNuxtApp()

  const setStart = (): void => {
    options?.onStart?.()

    if (!payload.skipStore) {
      if (!payload.background) {
        bookingStore.setLoading(true)
        bookingStore.setBookings(null)
        bookingStore.setCountdown(null)
      } else {
        if (!bookingStore.isLoadingDone) {
          bookingStore.setLoading(true)
          bookingStore.setBookings(null)
          bookingStore.setCountdown(null)
        }
      }
    }
  }

  const setSuccess = (bookings: Booking[]): void => {
    if (!payload.skipStore) {
      bookingStore.setBookings(bookings)
      bookingStore.setLoading(false)
      bookingStore.setLoadingDone(true)
      bookingStore.setBookingTrigger()
    }

    options?.onSuccess?.(bookings)
  }

  const setError = (err: any): void => {
    console.error(err)

    if (!payload.skipStore) {
      bookingStore.setBookings(null)
      bookingStore.setLoadingDone(false)
    }
  }

  const url = useCsRoutes('bookings', pick({
    '[discipline_id]': payload.disciplineId,
    status: listBookingRequestStatuses,
    with_delayed_canceled: 0,
    sort_by: ['start', 'id'],
    sort_direction: SortDirection.Asc,
    from: payload.from,
    to: payload.to,
  }))

  const bookingStore = useBookingStore()

  setStart()

  try {
    const { data } = await $axios.get(url!)
    const bookings = data.data?.data || []

    if (isFullArray(bookings)) {
      setSuccess(bookings)
      return bookings
    } else {
      const url = useCsRoutes('bookings', pick({
        '[discipline_id]': payload.disciplineId,
        status: listFallbackBookingRequestStatuses,
        with_delayed_canceled: 0,
        sort_by: ['start', 'id'],
        sort_direction: SortDirection.Desc,
        from: payload.from,
        to: payload.to,
      }))

      try {
        const { data } = await $axios.get(url!)
        const bookings = data.data?.data || []

        setSuccess(bookings)
        return bookings
      } catch (err) {
        setError(err)
        return null
      }
    }
  } catch (err) {
    setError(err)
    return null
  }
}
